/**
 * Zapier Integration Marketplace tab.
 *
 * Renders every registered CRM/messaging/email/accounting connector
 * grouped by category. Each tile shows live/mock status, description,
 * docs link, and current usage count (# integrations sync-targeting it).
 *
 * Consumes: GET /api/admin/zapier/marketplace
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Grid3X3, ExternalLink, Loader2, RefreshCcw, CheckCircle2, Circle,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const CATEGORY_LABELS = {
  crm: 'CRM',
  messaging: 'Messaging',
  email: 'Email Marketing',
  sheet: 'Sheets & Storage',
  accounting: 'Accounting',
};

const CATEGORY_ORDER = ['crm', 'messaging', 'email', 'sheet', 'accounting'];

function ConnectorTile({ connector }) {
  const isLive = connector.live;
  return (
    <div
      className="border rounded-xl p-4 hover:shadow-md transition bg-white flex flex-col gap-2"
      data-testid={`marketplace-tile-${connector.key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{connector.display_name}</div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-0.5">
            {CATEGORY_LABELS[connector.category] || connector.category}
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
            isLive
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-slate-100 text-slate-600'
          }`}
          data-testid={`marketplace-status-${connector.key}`}
        >
          {isLive ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
          {isLive ? 'live' : 'mock'}
        </span>
      </div>
      {connector.description && (
        <p className="text-xs text-slate-600 line-clamp-2">{connector.description}</p>
      )}
      <div className="mt-auto flex items-center justify-between pt-2 border-t">
        <span
          className="text-xs text-slate-500"
          data-testid={`marketplace-usage-${connector.key}`}
        >
          {connector.active_integrations || 0} active
        </span>
        {connector.docs_url && (
          <a
            href={connector.docs_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-indigo-600 hover:underline inline-flex items-center gap-1"
            data-testid={`marketplace-docs-${connector.key}`}
          >
            Docs <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
}

export default function MarketplaceTab() {
  const [data, setData] = useState({ marketplace: {}, connectors: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/zapier/marketplace`, AUTH());
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Marketplace load failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = Object.keys(data.marketplace || {}).sort(
    (a, b) => CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b),
  );

  const liveCount = (data.connectors || []).filter((c) => c.live).length;
  const totalCount = (data.connectors || []).length;

  return (
    <div className="space-y-5" data-testid="marketplace-tab">
      <Card className="p-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Grid3X3 className="w-6 h-6 text-indigo-600" />
          <div>
            <h3 className="text-lg font-semibold">Integration Marketplace</h3>
            <p className="text-sm text-slate-600">
              {totalCount} connectors registered · <span className="text-emerald-700 font-medium">{liveCount} live</span> · {totalCount - liveCount} in mock mode
            </p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="marketplace-refresh">
          <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </Card>

      {loading && (
        <div className="text-center py-10 text-slate-500" data-testid="marketplace-loading">
          <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading marketplace…
        </div>
      )}

      {!loading && categories.length === 0 && (
        <Card className="p-8 text-center text-slate-500" data-testid="marketplace-empty">
          No connectors registered.
        </Card>
      )}

      {categories.map((cat) => (
        <div key={cat} data-testid={`marketplace-category-${cat}`}>
          <div className="flex items-center gap-2 mb-3">
            <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
              {CATEGORY_LABELS[cat] || cat}
            </h4>
            <Badge variant="outline" className="text-[10px]">
              {(data.marketplace[cat] || []).length}
            </Badge>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {(data.marketplace[cat] || []).map((c) => (
              <ConnectorTile key={c.key} connector={c} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
